import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Activity } from '@/types';
import { getActivityCategoryLabel } from '@/services/activityService';
import { getLowestPriceFromTiers } from '@/services/pricingService';
import { supabase } from '@/integrations/supabase/client';

interface VenueCardProps {
  activities: Activity[];
  featured?: boolean;
}

const VenueCard = ({ activities, featured = false }: VenueCardProps) => {
  const [venueName, setVenueName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  if (activities.length === 0) return null;

  // Use first activity for venue info; prefer venue main image from Paramètres
  const firstActivity = activities[0];
  const city = firstActivity.city;
  const address = firstActivity.address;
  const mainImage = (firstActivity as { venueMainImage?: string | null }).venueMainImage ?? firstActivity.mainImage;

  // Fetch venue display name from professionals - never use activity name
  useEffect(() => {
    let cancelled = false;
    const fetchVenueName = async () => {
      if (firstActivity.professionalId) {
        const { data } = await supabase
          .from('professionals')
          .select('business_name, display_name')
          .eq('id', firstActivity.professionalId)
          .single();

        if (!cancelled) {
          setVenueName(data ? (data.display_name || data.business_name) : null);
        }
      } else {
        if (!cancelled) setVenueName(null);
      }
      if (!cancelled) setLoading(false);
    };
    fetchVenueName();
    return () => { cancelled = true; };
  }, [firstActivity.professionalId]);
  
  // Get unique category display labels (uses custom name for "autre")
  const categoriesDisplay = Array.from(new Set(activities.map(a => getActivityCategoryLabel(a))));

  // Minimum price across all activities at this venue
  const minPrice = Math.min(
    ...activities.map((a) => getLowestPriceFromTiers(a.priceTiers)).filter((p) => p > 0),
    Infinity
  );
  const hasValidPrice = minPrice > 0 && minPrice !== Infinity;

  const navigateTo = firstActivity.professionalId
    ? `/venue/${firstActivity.professionalId}`
    : `/activity/${firstActivity.slug}`;

  return (
    <Link 
      to={navigateTo}
      className={`
        block group rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow
        ${featured ? 'lg:flex lg:h-60' : ''}
      `}
    >
      <div className={`relative ${featured ? 'lg:w-1/3' : 'h-40'} overflow-hidden`}>
        <img
          src={mainImage}
          alt={venueName ?? 'Établissement'}
          width={400}
          height={160}
          loading="lazy"
          className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
        />
        <Badge className="absolute top-3 left-3 bg-brand-primary hover:bg-brand-primary">
          {activities.length} activité{activities.length > 1 ? 's' : ''}
        </Badge>
      </div>
      
      <div className={`p-4 ${featured ? 'lg:w-2/3 lg:p-6' : ''}`}>
        <div className="flex justify-between items-start mb-2">
          <h3 className={`font-bold ${featured ? 'text-xl' : 'text-lg'} text-gray-900 group-hover:text-brand-primary transition-colors`}>
            {loading ? (
              <span className="inline-block w-24 h-5 bg-gray-200 rounded animate-pulse" aria-hidden />
            ) : (
              venueName ?? 'Établissement'
            )}
          </h3>
        </div>
        
        <div className="flex flex-wrap gap-2 mb-3">
          {categoriesDisplay.slice(0, 3).map((label, index) => (
            <Badge key={index} variant="outline" className="bg-gray-50 text-xs">
              {label}
            </Badge>
          ))}
          {categoriesDisplay.length > 3 && (
            <Badge variant="outline" className="bg-gray-50 text-xs">
              +{categoriesDisplay.length - 3}
            </Badge>
          )}
        </div>
        
        <div className="flex items-center text-gray-500 mb-3">
          <MapPin size={16} className="mr-1" />
          <span className="text-sm">{firstActivity.postalCode ? `${city}, ${firstActivity.postalCode}` : city}</span>
        </div>

        {hasValidPrice && (
          <p className="text-brand-primary font-semibold text-sm mb-2">
            À partir de {minPrice.toFixed(0)}€
          </p>
        )}

        <div className="text-brand-primary font-semibold text-sm">
          Voir les {activities.length} activité{activities.length > 1 ? 's' : ''} →
        </div>
      </div>
    </Link>
  );
};

export default VenueCard;
